/**
 * Renders a per-archetype preview of a Studio Builder draft / saved project.
 * Pure presentational - takes (archetype, config) and renders a tasteful
 * read-only preview using the brand styles.
 */

const CHANNEL_LABEL = {
  instagram: "Instagram",
  linkedin: "LinkedIn",
  email: "Email",
  tiktok: "TikTok",
  twitter: "X / Twitter",
  facebook: "Facebook",
};

function Field({ label, value }) {
  return (
    <div className="border border-neutral-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-400">{label}</p>
      <p className="text-sm text-neutral-700 mt-1.5 leading-relaxed break-words">{value || "-"}</p>
    </div>
  );
}

function LeadCapturePreview({ config }) {
  const fields = Array.isArray(config?.fields) ? config.fields : [];
  return (
    <div
      data-testid="preview-lead_capture"
      className="border border-neutral-200 bg-white p-6 sm:p-10 max-w-2xl"
    >
      <p className="overline">{config?.cta || "Submit"}</p>
      <h3 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight">
        {config?.headline || "Talk to us"}
      </h3>
      {config?.sub && (
        <p className="text-neutral-500 mt-3 leading-relaxed">{config.sub}</p>
      )}
      <div className="mt-6 space-y-4">
        {fields.map((f, i) => (
          <div key={f.key || i}>
            <label className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">
              {f.label || f.key}
              {f.required && <span className="text-gold ml-1">*</span>}
            </label>
            {f.type === "textarea" ? (
              <textarea
                disabled
                rows={3}
                className="w-full bg-white border border-neutral-200 px-4 py-3 text-sm cursor-not-allowed text-neutral-400"
                placeholder={f.label || ""}
              />
            ) : f.type === "select" ? (
              <select disabled className="w-full bg-white border border-neutral-200 px-4 py-3 text-sm cursor-not-allowed text-neutral-400">
                <option>{(f.options || [])[0] || "Choose..."}</option>
              </select>
            ) : (
              <input
                disabled
                type={f.type || "text"}
                className="w-full bg-white border border-neutral-200 px-4 py-3 text-sm cursor-not-allowed text-neutral-400"
                placeholder={f.label || ""}
              />
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        disabled
        className="btn-aurora mt-7 w-full justify-center cursor-not-allowed opacity-75"
      >
        {config?.cta || "Send"}
      </button>
      {config?.success_message && (
        <p className="text-xs text-neutral-400 mt-4">
          On submit: &ldquo;{config.success_message}&rdquo;
        </p>
      )}
    </div>
  );
}

function ContentPackPreview({ config }) {
  const posts = Array.isArray(config?.posts) ? config.posts : [];
  return (
    <div data-testid="preview-content_pack" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Topic" value={config?.topic} />
        <Field label="Audience" value={config?.audience} />
        <Field label="Tone" value={config?.tone} />
      </div>
      <div>
        <p className="overline mb-4">Calendar ({posts.length} posts)</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {posts.map((p, i) => (
            <div
              key={i}
              className="border border-neutral-200 bg-white p-5"
            >
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em]">
                <span className="text-gold">{p.day || `Day ${i + 1}`}</span>
                <span className="text-neutral-500">{CHANNEL_LABEL[p.channel] || p.channel}</span>
              </div>
              <p className="text-sm text-neutral-700 mt-3 leading-relaxed whitespace-pre-wrap">{p.body}</p>
              {p.image_prompt && (
                <p className="text-xs text-neutral-400 italic mt-3 border-t border-neutral-200/70 pt-3">
                  Image: {p.image_prompt}
                </p>
              )}
            </div>
          ))}
          {posts.length === 0 && (
            <p className="text-sm text-neutral-400">No posts drafted yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function BrandKitPreview({ config }) {
  const palette = config?.palette || {};
  const fonts = config?.fonts || {};
  const taglines = Array.isArray(config?.taglines) ? config.taglines : [];
  const voice = Array.isArray(config?.voice) ? config.voice : [];
  return (
    <div data-testid="preview-brand_kit" className="space-y-6">
      <div className="border border-neutral-200 bg-white p-6 sm:p-8">
        <p className="overline">{config?.brand_name || "Brand"}</p>
        <h3 className="font-serif text-3xl sm:text-4xl mt-3 italic text-aurora">
          {config?.mission || "Brand mission"}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {["primary", "secondary", "accent"].map((slot) => (
          <div key={slot} className="border border-neutral-200 overflow-hidden">
            <div
              className="h-28"
              style={{ backgroundColor: palette[slot] || "#0a0a0a" }}
            />
            <div className="p-4 bg-white">
              <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-400">{slot}</p>
              <p className="text-sm text-neutral-700 mt-1 font-mono">{palette[slot] || "-"}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Display font" value={fonts.display} />
        <Field label="Body font" value={fonts.body} />
      </div>

      <div className="border border-neutral-200 bg-white p-5">
        <p className="overline">Voice</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {voice.map((v, i) => (
            <span
              key={i}
              className="text-xs uppercase tracking-[0.18em] border border-gold/30 text-gold px-3 py-1.5"
            >
              {v}
            </span>
          ))}
          {voice.length === 0 && <span className="text-sm text-neutral-400">No voice traits set.</span>}
        </div>
      </div>

      <div className="border border-neutral-200 bg-white p-5">
        <p className="overline">Taglines</p>
        <ul className="mt-3 space-y-2">
          {taglines.map((t, i) => (
            <li key={i} className="text-sm text-neutral-700 leading-relaxed before:content-['·'] before:text-gold before:mr-2">
              {t}
            </li>
          ))}
          {taglines.length === 0 && <li className="text-sm text-neutral-400">No taglines drafted.</li>}
        </ul>
      </div>
    </div>
  );
}

function RecommenderPreview({ config }) {
  const questions = Array.isArray(config?.questions) ? config.questions : [];
  const outputs = Array.isArray(config?.outputs) ? config.outputs : [];
  return (
    <div data-testid="preview-recommender" className="space-y-6">
      <div className="border border-neutral-200 bg-white p-6 sm:p-8">
        <h3 className="font-serif text-3xl sm:text-4xl leading-tight">
          {config?.title || "Find your fit"}
        </h3>
        {config?.intro && (
          <p className="text-neutral-500 mt-3 leading-relaxed">{config.intro}</p>
        )}
      </div>

      <div>
        <p className="overline mb-3">Questions ({questions.length})</p>
        <div className="space-y-4">
          {questions.map((q, i) => (
            <div key={i} className="border border-neutral-200 bg-white p-5">
              <p className="font-serif text-lg leading-snug">
                <span className="text-gold mr-2">{i + 1}.</span>
                {q.q || q.question || "Question"}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {(q.options || []).map((opt, j) => (
                  <span
                    key={j}
                    className="text-xs border border-neutral-200 px-3 py-1.5 text-neutral-600"
                    title={opt.score_for ? `→ ${opt.score_for}` : undefined}
                  >
                    {opt.label || opt}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="overline mb-3">Recommendations ({outputs.length})</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {outputs.map((o, i) => (
            <div key={i} className="border border-gold/30 bg-gold/[0.04] p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">{o.name}</p>
              <p className="text-sm text-neutral-700 mt-2 leading-relaxed">{o.blurb}</p>
              {o.when_to_pick && (
                <p className="text-xs text-neutral-500 italic mt-3 border-t border-neutral-200/70 pt-3">
                  Pick when: {o.when_to_pick}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProjectPreview({ archetype, config }) {
  if (!config) return null;
  if (archetype === "lead_capture") return <LeadCapturePreview config={config} />;
  if (archetype === "content_pack") return <ContentPackPreview config={config} />;
  if (archetype === "brand_kit") return <BrandKitPreview config={config} />;
  if (archetype === "recommender") return <RecommenderPreview config={config} />;
  return (
    <pre className="text-xs text-neutral-500 bg-white border border-neutral-200 p-5 overflow-auto">
      {JSON.stringify(config, null, 2)}
    </pre>
  );
}
